/**
 * Utilidades generales para la aplicación frontend
 *
 * @module utils
 * @version 1.0.0
 * @author Unolet
 * @copyright 2024
 */


import axios from 'axios';
import vars from "./vars";


/**
 * Instancia de Axios configurada para manejar CSRF tokens
 *
 * Uso:
 * apiAxios.get('/api/endpoint/')
 *   .then(response => { ... })
 *   .catch(error => { ... });
 */
const apiAxios = axios.create({
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken'
});
apiAxios.interceptors.request.use(config => {
  const csrfToken = document.cookie.replace(
      /(?:(?:^|.*;\s*)csrftoken\s*=\s*([^;]*).*$)|^.*$/,
      '$1'
  );
  config.headers['X-CSRFToken'] = csrfToken;
  return config;
});


/** * Formatea un número con comas como separadores de miles
 * @param value Número o cadena a formatear
 * @param decimalPlaces Número de decimales (por defecto 2)
 * @returns Cadena formateada
 */
function intcomma(value: number | string, decimalPlaces: number = 2): string {
  const num = Number(value);

  if (isNaN(num)) return '0.00'; // Valor no numérico, evita crash

  const parts = num.toFixed(decimalPlaces).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
}


/** * Formatea un número como moneda con símbolo y comas
 * @param value Número o cadena a formatear
 * @param currencySymbol Símbolo de moneda (por defecto '$')
 * @param decimalPlaces Número de decimales (por defecto 2)
 * @returns Cadena formateada como moneda
 */
function formatCurrency(value: number | string, currencySymbol: string = '$', decimalPlaces: number = 2): string {
  return `${currencySymbol}${intcomma(value, decimalPlaces)}`;
}


/**
 * Calcula el impuesto sobre una cantidad dada una tasa impositiva
 * @param amount
 * @param taxRate
 * @returns number
 */
function calculateTax(amount: number, taxRate: number): number {
  return amount * (taxRate / 100);
}


/** * Devuelve el importe extrayendo el impuesto incluido
 * @param totalAmount
 * @param taxRate
 * @returns number
 */
function extractTax(totalAmount: number, taxRate: number): number {
  return totalAmount / (1 + taxRate / 100);
}


/**
 * Normaliza errores de Django / DRF a:
 * { [campo]: string[] }
 *
 * Soporta:
 * - string
 * - string[]
 * - number / boolean
 * - null / undefined
 * - { campo: string | string[] | object | object[] }
 * - object[] mezclados (strings, objetos, etc.)
 * - arrays raíz (["error"], [{ campo: "error" }])
 */
function parseErrors(input: unknown): Record<string, string[]> {
  const errors: Record<string, string[]> = {};

  const normalizeKey = (field?: string): string =>
    !field || field === '__all__' || field === 'non_field_errors'
      ? 'non_field_errors'
      : field;

  const add = (field: string | undefined, value: unknown) => {
    const key = normalizeKey(field);

    if (!errors[key]) errors[key] = [];

    if (typeof value === 'string') {
      errors[key].push(value);
    } else if (Array.isArray(value)) {
      value.forEach(v => add(field, v));
    } else if (value && typeof value === 'object') {
      walk(value, field);
    } else if (value != null) {
      errors[key].push(String(value));
    }
  };

  const walk = (data: unknown, parentField?: string) => {
    if (data == null) return;

    // Caso: string directo
    if (typeof data === 'string') {
      return add(parentField, data);
    }

    // Caso: array raíz o anidado
    if (Array.isArray(data)) {
      return data.forEach(item => walk(item, parentField));
    }

    // Caso: objeto
    if (typeof data === 'object') {
      const entries = Object.entries(data as Record<string, unknown>);

      // Caso especial: objeto vacío → ignorar
      if (entries.length === 0) return;

      for (const [field, value] of entries) {
        // Si el valor es primitivo → asignar directo
        if (
          typeof value === 'string' ||
          typeof value === 'number' ||
          typeof value === 'boolean' ||
          value == null
        ) {
          add(field, value);
        } else {
          walk(value, field);
        }
      }
      return;
    }

    // fallback
    add(parentField, data);
  };

  walk(input);

  return errors;
}


interface NotifyOptions {
  title: string;
  body?: string;
  icon?: string | null;
  audio?: string | null;
  action?: (() => void) | null;
  timeout?: number;
  force?: boolean;
}


/**
 * @description Muestra una notificación de escritorio con sonido opcional.
 * @param title Título de la notificación.
 * @param body Cuerpo del mensaje.
 * @param icon URL del icono de la notificación.
 * @param audio URL del sonido a reproducir.
 * @param action Función a ejecutar al hacer clic en la notificación.
 * @param timeout Tiempo en ms para cerrar la notificación automáticamente (0 = no cerrar).
 */
function notify({
  title = "Notificación de Unolet",
  body,
  icon,
  audio,
  action = null,
  timeout = 7000,
}: NotifyOptions): void {
  if (!body) return;
  if (!title) title = "Notificación de Unolet";
  if (!icon) icon = "/static/img/base/logo.png";
  if (!audio) audio = "/static/audio/tap-notification.mp3";

  const notification = new Notification(title, { body, icon });

  if (audio) {
    const audioObj = new Audio(audio);
    audioObj.play().catch(() => {});
  }

  if (action) {
    notification.onclick = () => action();
  }

  if (timeout > 0) {
    setTimeout(() => notification.close(), timeout);
  }
}


/**
 * @description Solicita permiso y emite la notificación si es posible.
 * @param options Opciones de la notificación (ver NotifyOptions).
 * @return Promise<void>
 */
async function notifyMe(options: NotifyOptions): Promise<void> {
  if (!("Notification" in window)) return;

  const permission = Notification.permission;

  if (permission === "granted") {
    notify(options);
    return;
  }

  if (permission === "denied" && !options.force) return;

  const result = await Notification.requestPermission();

  if (result === "granted") {
    notify({
      title: "¡Notificaciones activadas!",
      body: "Ahora recibirás notificaciones de Unolet.",
      timeout: 3000,
    });
    notify(options);
  }
}


/**
 * Muestra una alerta o mensaje en la página con soporte para Bootstrap y fallback configurable.
 * @param msg Mensaje a mostrar.
 * @param title Título opcional del mensaje.
 * @param type Tipo de alerta (info [default], danger, warning, success).
 * @param alt_out Alternativa si no existe el contenedor ('alert' o 'console').
 * @param id Identificador HTML del elemento creado.
 */
function showMessage(
  msg: string = "",
  title: string = "",
  type: "info" | "danger" | "warning" | "success" = "info",
  alt_out: "alert" | "console" = "alert",
  id: string = "id_message"
): void {
  const container = document.getElementById("content-messages");

  // Construcción del mensaje con título
  const messageText = title ? `<strong>${title}</strong>: ${msg}` : msg;

  // Determinar ícono según tipo
  const iconMap = {
    info: "bi bi-info-circle-fill",
    warning: "bi bi-exclamation-triangle-fill",
    danger: "bi bi-x-circle-fill",
    success: "bi bi-check-circle-fill",
  };
  const icon = iconMap[type] || iconMap.info;

  if (container) {
    const html = `
      <div id="${id}" class="alert alert-${type} alert-dismissible fade show alert-link shadow animate__animated animate__bounceIn" role="alert">
        <i class="${icon} me-1"></i>${messageText}
        <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
      </div>
    `;
    container.innerHTML = html;
  } else {
    const fallbackMsg = `${title ? title + ": " : ""}${msg}`;
    alt_out === "alert" ? alert(fallbackMsg) : console.log(fallbackMsg);
  }
}


/**
 * Oculta el mensaje mostrado en el contenedor principal.
 */
function hideMessage(): void {
  const container = document.getElementById("content-messages");
  if (container) container.innerHTML = "";
}


/** * Muestra un modal de Bootstrap dado su ID.
 * @param modalId ID del modal a mostrar.
 */
function showModal(modalId: string): void {
  const modalElement = document.getElementById(modalId);
  if (modalElement) {
    const modal = new (window as any).bootstrap.Modal(modalElement);
    modal.show();
  } else {
    console.error(`Modal con id "${modalId}" no encontrado.`);
  }
}


/**
 * Exporta una tabla HTML a un archivo Excel (.xls) usando un formato base64.
 * @param tableSelector Selector CSS de la tabla a exportar (ej. '#my-table').
 * @param filename Nombre del archivo a generar (por defecto 'unolet.xls').
 */
function exportTableToExcel(tableSelector: string, filename: string = "unolet"): void {
  // Función para convertir una cadena a base64 de forma segura
  const base64 = (s: string): string => window.btoa(unescape(encodeURIComponent(s)));

  // Función para reemplazar placeholders en la plantilla
  const format = (template: string, context: Record<string, string>): string =>
    template.replace(/{(\w+)}/g, (_, key) => context[key] || "");

  // Buscar la tabla en el DOM
  const table = document.querySelector<HTMLTableElement>(tableSelector);
  if (!table) {
    console.error(`No se encontró ninguna tabla con el selector: ${tableSelector}`);
    return;
  }

  // Asegurar extensión del archivo
  const fileWithExt = filename.endsWith(".xls") ? filename : `${filename}.xls`;

  // Plantilla HTML compatible con Excel
  const template = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office"
          xmlns:x="urn:schemas-microsoft-com:office:excel"
          xmlns="http://www.w3.org/TR/REC-html40">
      <head>
        <!--[if gte mso 9]>
          <xml>
            <x:ExcelWorkbook>
              <x:ExcelWorksheets>
                <x:ExcelWorksheet>
                  <x:Name>{worksheet}</x:Name>
                  <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
                </x:ExcelWorksheet>
              </x:ExcelWorksheets>
            </x:ExcelWorkbook>
          </xml>
        <![endif]-->
        <meta http-equiv="content-type" content="text/plain; charset=UTF-8"/>
      </head>
      <body>
        <table>{table}</table>
      </body>
    </html>`;

  // Crear contexto
  const ctx = {
    worksheet: fileWithExt.replace(".xls", ""),
    table: table.innerHTML,
  };

  // Construir contenido en base64
  const uri = "data:application/vnd.ms-excel;base64,";
  const content = uri + base64(format(template, ctx));

  // Crear enlace de descarga
  const downloadLink = document.createElement("a");
  downloadLink.href = content;
  downloadLink.download = fileWithExt;

  // Ejecutar descarga
  document.body.appendChild(downloadLink);
  downloadLink.click();
  document.body.removeChild(downloadLink);
}



async function sendFeedback(description: string) {
  try {
    const response = await apiAxios.post('/api/feedback/', { description });
    return response.data;
  } catch (error) {
    console.error("Error enviando feedback:", error);
    throw error;
  }
}



// -----------------------------------------------------------------------------
// TODO: Deprecatd legacy code compatibility (to be removed in future versions)
// -----------------------------------------------------------------------------



/**
 * Comprueba que el valor pasado tenga un valor verdadero.
 * @param value Valor que se desea evaluar.
 */
function is(value: any): boolean {
  if (value == undefined) {
    return false;
  }
  if (isNaN(value)) {
    return false;
  }
  if (value) {
    return true;
  }
  return false;
}


/**
 * Comprueba que todos sus valores sean verdaderos.
 * @param a Primer valor a comparar.
 * @param b Segundo valor a comparar.
 */
function and(a: any, b: any): boolean {
  if (!is(a)) {
    return false;
  }
  if (!is(b)) {
    return false;
  }
  return true;
}


/**
 * @returns any.toString()
 */
function str(any: any): string {
  return any.toString();
}


function firstOf(a: any, b: any = null): any {
  if (is(a)) {
    return a;
  }
  return b;
}


/**
 * Intenta converir el valor en un número entero.
 * @param number número o texto a convertir.
 * @param alt_return si falla la conversión, devolverá este valor en su lugar.
 * @returns parseInt(number) or alt_return
 */
function int(number: any, alt_return: number = 0): number {
  return firstOf(parseInt(number), alt_return);
}


/**
 * Intenta converir el valor en un número de coma flotante.
 * @param number número o texto a convertir.
 * @param alt_return si falla la conversión, devolverá este valor en su lugar.
 * @returns parseFloat(number) or alt_return
 */
function float(number: any, alt_return: number = 0): number {
  return firstOf(parseFloat(number), alt_return);
}


function getById(id: string): HTMLElement | null {
  return document.getElementById(id);
}


function goToURL(url: string, newtab = false) {
  if (window.getSelection() && window.getSelection()!.toString()) {
    return;
  }
  location.href = url;
}


function calcularImpuesto(importe: number, impuesto_type = vars.PORCENTAJE, impuesto_value = 0) {
  if (impuesto_type == vars.FIJO) return impuesto_value;
  if (impuesto_type == vars.PORCENTAJE) return calculateTax(importe, impuesto_value);
  return 0;
}


function extraerImpuesto(importe = 0, impuesto_type = vars.PORCENTAJE, impuesto_value = 0) {
  importe = parseFloat(importe.toString());
  impuesto_value = parseFloat(impuesto_value.toString());
  if (impuesto_type == vars.PORCENTAJE) return extractTax(importe, impuesto_value);
  if (impuesto_type == vars.FIJO) return importe - impuesto_value;
  return importe;
}


function orderBy(field_name: string, param_name="order_by") {
  param_name = param_name || "order_by";
  if (location.search) {
    location.href = location.pathname + location.search + `&${param_name}=${field_name}`;
  } else {
    location.href = location.pathname + `?${param_name}=${field_name}`;
  }
}


function translate(text: string): string {
  return text;
}

const _ = translate

// -----------------------------------------------------------------------------
// Fin de código legado
// -----------------------------------------------------------------------------


export {
  _, and, apiAxios, calcularImpuesto, calculateTax, exportTableToExcel, extractTax, extraerImpuesto, firstOf, float, formatCurrency, getById,
  goToURL, hideMessage, int, intcomma, is, notifyMe, orderBy, parseErrors, sendFeedback, showMessage, showModal, str, translate
};


